import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Pencil, Trash2, Upload } from 'lucide-react'
import { guildApi } from '@/lib/guildApi'
import { describeError } from '@/lib/auth'
import { stickerImageUrl } from '@/lib/markdown'
import { cx } from '@/lib/cx'
import type { Guild, Sticker } from '@/types/api'
import { Field } from '@/pages/AuthPage'
import auth from '@/pages/AuthPage.module.css'
import layer from '../settings/SettingsLayer.module.css'
import gs from './GuildSettingsLayer.module.css'
import styles from './StickersSection.module.css'
import { useGuildLimits } from '@/lib/guildLimits'

const MAX_BYTES = 512 * 1024

/** Настройки сервера → «Стикеры»: PNG/APNG/GIF до 512 КБ с именем, описанием и тегами; правка, удаление. */
export function StickersSection({ guild, canManage }: { guild: Guild; canManage: boolean }) {
  const qc = useQueryClient()
  const stickers = useQuery({ queryKey: ['stickers', guild.id], queryFn: () => guildApi.stickers(guild.id) })
  // сколько можно — по уровню бустов сервера, как считает сервер
  const slots = useGuildLimits(guild.premium_tier).stickers
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState<{ file: string; name: string; description: string; tags: string } | null>(null)
  const [editing, setEditing] = useState<{ id: string; name: string; description: string; tags: string } | null>(null)
  const fileInput = useRef<HTMLInputElement | null>(null)
  const refresh = () => void qc.invalidateQueries({ queryKey: ['stickers', guild.id] })
  const fail = (err: unknown) => {
    const d = describeError(err)
    setError(d.fields.name ?? d.fields.file ?? d.fields.description ?? d.fields.tags ?? d.message)
  }
  const create = useMutation({ mutationFn: (body: { name: string; description: string; tags: string; file: string }) => guildApi.createSticker(guild.id, body), onSuccess: () => { setPending(null); refresh() }, onError: fail })
  const update = useMutation({ mutationFn: (v: { id: string; name: string; description: string; tags: string }) => guildApi.updateSticker(guild.id, v.id, { name: v.name, description: v.description, tags: v.tags }), onSuccess: () => { setEditing(null); refresh() }, onError: fail })
  const remove = useMutation({ mutationFn: (id: string) => guildApi.deleteSticker(guild.id, id), onSuccess: refresh, onError: fail })

  const choose = (files: FileList | null) => {
    const f = files?.[0]
    if (!f) return
    setError(null)
    if (f.size > MAX_BYTES) {
      setError(`${f.name}: больше 512 КБ`)
      return
    }
    const reader = new FileReader()
    reader.onload = () => setPending({ file: String(reader.result), name: f.name.replace(/\.[^.]+$/, '').slice(0, 30), description: '', tags: '' })
    reader.readAsDataURL(f)
  }

  const list: Sticker[] = stickers.data ?? []
  return (
    <section>
      <h1 className={layer.sectionTitle}>Стикеры — {list.length} из {slots}</h1>
      <ul className={layer.points}>
        <li>PNG, APNG или GIF до 512 КБ, лучше 320×320</li>
        <li>имя от 2 до 30 символов; описание и теги помогают найти стикер в пикере</li>
        <li>отправляются кнопкой стикера рядом с полем ввода</li>
      </ul>
      {canManage ? (
        <div className={layer.buttonRow}>
          <input ref={fileInput} type="file" accept="image/png,image/gif,image/apng" hidden onChange={(e) => { choose(e.target.files); e.target.value = '' }} />
          <button type="button" className={layer.button} disabled={create.isPending || list.length >= slots} onClick={() => fileInput.current?.click()}><Upload size={16} /> Загрузить стикер</button>
        </div>
      ) : null}
      {pending ? (
        <form className={styles.form} onSubmit={(e) => { e.preventDefault(); create.mutate(pending) }} aria-label="Новый стикер">
          <img src={pending.file} alt="" className={styles.preview} />
          <div className={styles.formFields}>
            <Field label="Имя" htmlFor="st-name">
              <input id="st-name" className={auth.input} value={pending.name} maxLength={30} onChange={(e) => setPending({ ...pending, name: e.target.value })} />
            </Field>
            <Field label="Описание" htmlFor="st-desc">
              <input id="st-desc" className={auth.input} value={pending.description} maxLength={100} onChange={(e) => setPending({ ...pending, description: e.target.value })} />
            </Field>
            <Field label="Теги" htmlFor="st-tags" hint="Через запятую: по ним работает поиск">
              <input id="st-tags" className={auth.input} value={pending.tags} maxLength={200} onChange={(e) => setPending({ ...pending, tags: e.target.value })} />
            </Field>
            <div className={layer.buttonRow}>
              <button type="submit" className={layer.button} disabled={create.isPending || pending.name.trim().length < 2}>{create.isPending ? 'Загружаем…' : 'Добавить'}</button>
              <button type="button" className={gs.smallButton} onClick={() => setPending(null)}>Отмена</button>
            </div>
          </div>
        </form>
      ) : null}
      {error ? <p className={gs.error} role="alert">{error}</p> : null}
      {stickers.isPending ? <p className={layer.text}>Загружаем…</p> : null}
      {!stickers.isPending && list.length === 0 ? <p className={layer.text}>Стикеров пока нет.</p> : null}
      <ul className={styles.grid}>
        {list.map((s) => (
          <li key={s.id} className={styles.card}>
            <img src={s.url ?? stickerImageUrl(s)} alt={s.name} className={styles.img} />
            {editing?.id === s.id ? (
              <form className={styles.edit} onSubmit={(e) => { e.preventDefault(); update.mutate(editing) }}>
                <input className={styles.editInput} value={editing.name} maxLength={30} aria-label="Имя" onChange={(e) => setEditing({ ...editing, name: e.target.value })} autoFocus />
                <input className={styles.editInput} value={editing.description} maxLength={100} placeholder="Описание" aria-label="Описание" onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
                <input className={styles.editInput} value={editing.tags} maxLength={200} placeholder="Теги" aria-label="Теги" onChange={(e) => setEditing({ ...editing, tags: e.target.value })} />
                <div className={styles.editActions}>
                  <button type="submit" className={gs.smallButton} disabled={update.isPending}>Сохранить</button>
                  <button type="button" className={gs.smallButton} onClick={() => setEditing(null)}>Отмена</button>
                </div>
              </form>
            ) : (
              <>
                <span className={styles.name}>{s.name}</span>
                {s.description ? <span className={styles.desc}>{s.description}</span> : null}
                {s.format_type === 4 ? <span className={styles.badge}>GIF</span> : s.format_type === 2 ? <span className={styles.badge}>APNG</span> : null}
              </>
            )}
            {canManage && editing?.id !== s.id ? (
              <span className={styles.actions}>
                <button type="button" className={cx(gs.smallButton, styles.action)} data-tooltip="Изменить" aria-label={`Изменить стикер ${s.name}`} onClick={() => setEditing({ id: s.id, name: s.name, description: s.description, tags: s.tags })}><Pencil size={14} /></button>
                <button type="button" className={cx(gs.smallDanger, styles.action)} data-tooltip="Удалить" aria-label={`Удалить стикер ${s.name}`} disabled={remove.isPending} onClick={() => remove.mutate(s.id)}><Trash2 size={14} /></button>
              </span>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  )
}
